import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

export default function MembersPanelView({
  query,
  setQuery,
  loading,
  actionLoadingId,
  banner,
  deleteOpen,
  selectedUser,
  canManageUsers,
  canManageMemberships,
  loadMembers,
  filteredUsers,
  getFullName,
  getInitial,
  getPhoto,
  handleToggleStatus,
  openDeleteModal,
  closeDeleteModal,
  handleDeleteUser,
  membershipOpen,
  membershipLoading,
  membershipSubmitting,
  membershipUser,
  memberMemberships,
  activeMembership,
  plans,
  membershipForm,
  setMembershipForm,
  selectedPlan,
  openMembershipModal,
  closeMembershipModal,
  handleMembershipFieldChange,
  handleMembershipAccessToggle,
  handleAssignMembership,
  handleDeleteMembership,
}) {
  const availableAccesses = selectedPlan?.available_accesses || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.4}
        sx={{ mb: 1.8 }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar usuario"
            size="small"
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }} />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <Button
            onClick={loadMembers}
            variant="outlined"
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Recargar
          </Button>
        </Stack>
      </Stack>

      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            mb: 1.6,
          }}
        >
          {banner.message}
        </Alert>
      )}

      <Paper
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1.5fr 0.9fr",
              md: "1.65fr 1fr 0.9fr 0.8fr 1.45fr",
            },
            gap: 1,
            px: { xs: 1.6, md: 2 },
            py: 1.3,
            bgcolor: "#f8fafc",
            borderBottom: "1px solid rgba(148,163,184,0.16)",
          }}
        >
          <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
            Usuario
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Correo
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Teléfono
          </Typography>
          <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
            Estado
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Acción
          </Typography>
        </Box>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#64748b" }}>
              Cargando usuarios
            </Typography>
          </Stack>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography sx={{ fontSize: 13.4, fontWeight: 500, color: "#64748b" }}>
              No se encontraron usuarios.
            </Typography>
          </Box>
        ) : (
          filteredUsers.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1.5fr 0.9fr",
                  md: "1.65fr 1fr 0.9fr 0.8fr 1.45fr",
                },
                gap: 1,
                alignItems: "center",
                px: { xs: 1.6, md: 2 },
                py: 1.25,
                borderBottom: "1px solid rgba(148,163,184,0.12)",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  bgcolor: "rgba(248,250,252,0.8)",
                },
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar
                  src={getPhoto(item) || undefined}
                  alt={getFullName(item)}
                  imgProps={{ referrerPolicy: "no-referrer" }}
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    bgcolor: "rgba(59,130,246,0.12)",
                    color: "#1d4ed8",
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  {getInitial(item)}
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#0f172a",
                      fontSize: 13.4,
                      fontWeight: 600,
                      lineHeight: 1.35,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {getFullName(item)}
                  </Typography>

                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      color: "#64748b",
                      fontSize: 12.2,
                      fontWeight: 500,
                      lineHeight: 1.35,
                      mt: 0.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.email || "Sin correo"}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                sx={{
                  display: { xs: "none", md: "block" },
                  color: "#334155",
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.email || "Sin correo"}
              </Typography>

              <Typography
                sx={{
                  display: { xs: "none", md: "block" },
                  color: "#334155",
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.phone || "Sin teléfono"}
              </Typography>

              <Box>
                <Chip
                  label={item.is_active ? "Activo" : "Inactivo"}
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: "8px",
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: item.is_active
                      ? "rgba(59,130,246,0.10)"
                      : "rgba(100,116,139,0.12)",
                    color: item.is_active ? "#1d4ed8" : "#475569",
                  }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={1}
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: { md: "stretch", lg: "center" },
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                }}
              >
                {canManageMemberships && (
                  <Button
                    onClick={() => openMembershipModal(item)}
                    variant="outlined"
                    sx={{
                      height: 34,
                      px: 1.2,
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12.6,
                      color: "#334155",
                      borderColor: "rgba(51,65,85,0.18)",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "rgba(51,65,85,0.28)",
                        bgcolor: "rgba(148,163,184,0.08)",
                      },
                    }}
                  >
                    Membresías
                  </Button>
                )}

                {canManageUsers && (
                  <Button
                    onClick={() => handleToggleStatus(item)}
                    variant="outlined"
                    disabled={actionLoadingId === item.id}
                    sx={{
                      height: 34,
                      px: 1.2,
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12.6,
                      color: "#334155",
                      borderColor: "rgba(51,65,85,0.18)",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "rgba(51,65,85,0.28)",
                        bgcolor: "rgba(148,163,184,0.08)",
                      },
                    }}
                  >
                    {actionLoadingId === item.id
                      ? "Procesando"
                      : item.is_active
                      ? "Desactivar"
                      : "Activar"}
                  </Button>
                )}

                {canManageUsers && (
                  <Button
                    onClick={() => openDeleteModal(item)}
                    variant="outlined"
                    disabled={actionLoadingId === item.id}
                    startIcon={<DeleteRoundedIcon />}
                    sx={{
                      height: 34,
                      px: 1.2,
                      borderRadius: "9px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 12.6,
                      color: "#dc2626",
                      borderColor: "rgba(220,38,38,0.22)",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: "rgba(220,38,38,0.32)",
                        bgcolor: "rgba(220,38,38,0.06)",
                      },
                    }}
                  >
                    Eliminar
                  </Button>
                )}
              </Stack>
            </Box>
          ))
        )}
      </Paper>

      <Dialog
        open={deleteOpen}
        onClose={closeDeleteModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
            pb: 1,
          }}
        >
          Eliminar usuario
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
            <Avatar
              src={selectedUser ? getPhoto(selectedUser) || undefined : undefined}
              alt={selectedUser ? getFullName(selectedUser) : "Usuario"}
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{
                width: 42,
                height: 42,
                fontSize: 15,
                fontWeight: 700,
                bgcolor: "rgba(59,130,246,0.12)",
                color: "#1d4ed8",
                border: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              {selectedUser ? getInitial(selectedUser) : "U"}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0f172a",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedUser ? getFullName(selectedUser) : "Usuario"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12.6,
                  color: "#64748b",
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedUser?.email || "Sin correo"}
              </Typography>
            </Box>
          </Stack>

          <Typography
            sx={{
              fontSize: 14,
              color: "#475569",
              lineHeight: 1.6,
            }}
          >
            ¿Seguro que deseas eliminar a{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
              {selectedUser ? getFullName(selectedUser) : "este usuario"}
            </Box>
            ?
          </Typography>

          <Typography
            sx={{
              mt: 1.2,
              fontSize: 13,
              color: "#64748b",
              lineHeight: 1.5,
            }}
          >
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.2, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={closeDeleteModal}
            disabled={Boolean(actionLoadingId)}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleDeleteUser}
            variant="contained"
            disabled={!selectedUser || actionLoadingId === selectedUser?.id}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            {actionLoadingId === selectedUser?.id ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={membershipOpen}
        onClose={closeMembershipModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "20px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
            pb: 1,
          }}
        >
          Gestionar membresías
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          {membershipUser && (
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
              <Avatar
                src={getPhoto(membershipUser) || undefined}
                alt={getFullName(membershipUser)}
                imgProps={{ referrerPolicy: "no-referrer" }}
                sx={{
                  width: 42,
                  height: 42,
                  fontSize: 15,
                  fontWeight: 700,
                  bgcolor: "rgba(59,130,246,0.12)",
                  color: "#1d4ed8",
                  border: "1px solid rgba(148,163,184,0.18)",
                }}
              >
                {getInitial(membershipUser)}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0f172a",
                    lineHeight: 1.3,
                  }}
                >
                  {getFullName(membershipUser)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12.6,
                    color: "#64748b",
                    lineHeight: 1.35,
                  }}
                >
                  {membershipUser.email || "Sin correo"}
                </Typography>
              </Box>
            </Stack>
          )}

          {membershipLoading ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#64748b" }}>
                Cargando membresías
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(148,163,184,0.18)",
                  boxShadow: "none",
                  p: 1.6,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14.2,
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1.2,
                  }}
                >
                  Asignar nueva membresía
                </Typography>

                <Stack spacing={1.2}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
                    <TextField
                      select
                      fullWidth
                      label="Plan"
                      value={membershipForm.plan_id}
                      onChange={(event) => {
                        const nextPlanId = event.target.value;
                        const nextPlan = plans.find(
                          (item) => String(item.id) === String(nextPlanId)
                        );

                        setMembershipForm((prev) => ({
                          ...prev,
                          plan_id: nextPlanId,
                          selected_accesses: [],
                          start_date: "",
                          end_date: "",
                          price_paid: nextPlan?.price || "",
                          visits_total:
                            nextPlan?.usage_type === "visits"
                              ? String(nextPlan.duration_value || "")
                              : "",
                        }));
                      }}
                      size="small"
                    >
                      {plans.map((plan) => (
                        <MenuItem key={plan.id} value={String(plan.id)}>
                          {plan.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      fullWidth
                      label="Fecha de inicio"
                      type="date"
                      size="small"
                      value={membershipForm.start_date}
                      onChange={(event) =>
                        handleMembershipFieldChange("start_date", event.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />

                    {selectedPlan?.usage_type === "calendar" ? (
                      <TextField
                        fullWidth
                        label="Fecha de fin"
                        type="date"
                        size="small"
                        value={membershipForm.end_date}
                        onChange={(event) =>
                          handleMembershipFieldChange("end_date", event.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        label="Precio pagado"
                        size="small"
                        value={membershipForm.price_paid}
                        onChange={(event) =>
                          handleMembershipFieldChange("price_paid", event.target.value)
                        }
                      />
                    )}
                  </Stack>

                  {selectedPlan?.usage_type === "calendar" && (
                    <TextField
                      fullWidth
                      label="Precio pagado"
                      size="small"
                      value={membershipForm.price_paid}
                      onChange={(event) =>
                        handleMembershipFieldChange("price_paid", event.target.value)
                      }
                    />
                  )}

                  {selectedPlan?.usage_type === "visits" && (
                    <TextField
                      fullWidth
                      label="Visitas totales"
                      size="small"
                      value={membershipForm.visits_total}
                      onChange={(event) =>
                        handleMembershipFieldChange("visits_total", event.target.value)
                      }
                    />
                  )}

                  {!!availableAccesses.length && (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {availableAccesses.map((access) => {
                        const active = membershipForm.selected_accesses.includes(access);

                        return (
                          <Chip
                            key={access}
                            label={access}
                            clickable
                            onClick={() => handleMembershipAccessToggle(access)}
                            sx={{
                              height: 30,
                              borderRadius: "9px",
                              fontSize: 12.2,
                              fontWeight: 600,
                              bgcolor: active
                                ? "rgba(59,130,246,0.10)"
                                : "rgba(100,116,139,0.12)",
                              color: active ? "#1d4ed8" : "#475569",
                              border: active
                                ? "1px solid rgba(59,130,246,0.16)"
                                : "1px solid transparent",
                            }}
                          />
                        );
                      })}
                    </Stack>
                  )}

                  <TextField
                    fullWidth
                    label="Notas"
                    size="small"
                    multiline
                    minRows={2}
                    value={membershipForm.notes}
                    onChange={(event) =>
                      handleMembershipFieldChange("notes", event.target.value)
                    }
                  />

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      onClick={handleAssignMembership}
                      variant="contained"
                      disabled={
                        membershipSubmitting ||
                        !membershipForm.plan_id ||
                        !membershipForm.start_date ||
                        !membershipForm.price_paid ||
                        membershipForm.selected_accesses.length === 0 ||
                        (selectedPlan?.usage_type === "calendar" && !membershipForm.end_date)
                      }
                      sx={{
                        height: 36,
                        px: 1.6,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.8,
                        bgcolor: "#0f172a",
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "#111827",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {membershipSubmitting ? "Asignando..." : "Asignar membresía"}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(148,163,184,0.18)",
                  boxShadow: "none",
                  p: 1.6,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14.2,
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1.2,
                  }}
                >
                  Membresía activa
                </Typography>

                {activeMembership ? (
                  <Stack spacing={0.8}>
                    <Typography sx={{ fontSize: 13.2, fontWeight: 700, color: "#0f172a" }}>
                      {activeMembership.plan?.name || "Sin plan"}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip
                        label={activeMembership.status}
                        size="small"
                        sx={{
                          height: 26,
                          borderRadius: "8px",
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: "rgba(59,130,246,0.10)",
                          color: "#1d4ed8",
                        }}
                      />
                      {Array.isArray(activeMembership.selected_accesses) &&
                        activeMembership.selected_accesses.map((access) => (
                          <Chip
                            key={access}
                            label={access}
                            size="small"
                            sx={{
                              height: 26,
                              borderRadius: "8px",
                              fontSize: 12,
                              fontWeight: 600,
                              bgcolor: "rgba(100,116,139,0.12)",
                              color: "#475569",
                            }}
                          />
                        ))}
                    </Stack>
                    <Typography sx={{ fontSize: 12.8, color: "#475569" }}>
                      Inicio: {activeMembership.start_date || "—"}
                    </Typography>
                    <Typography sx={{ fontSize: 12.8, color: "#475569" }}>
                      Fin: {activeMembership.end_date || "—"}
                    </Typography>
                    {activeMembership.plan?.usage_type === "visits" && (
                      <Typography sx={{ fontSize: 12.8, color: "#475569" }}>
                        Visitas: {activeMembership.visits_used}/{activeMembership.visits_total}
                      </Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 13.2, color: "#64748b" }}>
                    Este miembro no tiene una membresía activa.
                  </Typography>
                )}
              </Paper>

              <Paper
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(148,163,184,0.18)",
                  boxShadow: "none",
                  p: 1.6,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14.2,
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1.2,
                  }}
                >
                  Historial de membresías
                </Typography>

                {!memberMemberships.length ? (
                  <Typography sx={{ fontSize: 13.2, color: "#64748b" }}>
                    Este miembro aún no tiene membresías registradas.
                  </Typography>
                ) : (
                  <Stack spacing={1.2}>
                    {memberMemberships.map((membership, index) => (
                      <Box key={membership.id}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 13.4,
                                fontWeight: 700,
                                color: "#0f172a",
                              }}
                            >
                              {membership.plan?.name || "Sin plan"}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{ mt: 0.6 }}
                            >
                              <Chip
                                label={membership.status}
                                size="small"
                                sx={{
                                  height: 26,
                                  borderRadius: "8px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  bgcolor:
                                    membership.status === "active"
                                      ? "rgba(59,130,246,0.10)"
                                      : "rgba(100,116,139,0.12)",
                                  color:
                                    membership.status === "active" ? "#1d4ed8" : "#475569",
                                }}
                              />
                              {Array.isArray(membership.selected_accesses) &&
                                membership.selected_accesses.map((access) => (
                                  <Chip
                                    key={`${membership.id}-${access}`}
                                    label={access}
                                    size="small"
                                    sx={{
                                      height: 26,
                                      borderRadius: "8px",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      bgcolor: "rgba(100,116,139,0.12)",
                                      color: "#475569",
                                    }}
                                  />
                                ))}
                            </Stack>

                            <Typography sx={{ fontSize: 12.6, color: "#475569", mt: 0.8 }}>
                              Inicio: {membership.start_date || "—"} | Fin:{" "}
                              {membership.end_date || "—"}
                            </Typography>

                            {membership.plan?.usage_type === "visits" && (
                              <Typography sx={{ fontSize: 12.6, color: "#475569", mt: 0.4 }}>
                                Visitas: {membership.visits_used}/{membership.visits_total}
                              </Typography>
                            )}
                          </Box>

                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Button
                              onClick={() => handleDeleteMembership(membership.id)}
                              variant="outlined"
                              startIcon={<DeleteRoundedIcon />}
                              sx={{
                                height: 34,
                                px: 1.2,
                                borderRadius: "9px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 12.6,
                                color: "#dc2626",
                                borderColor: "rgba(220,38,38,0.22)",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                  borderColor: "rgba(220,38,38,0.32)",
                                  bgcolor: "rgba(220,38,38,0.06)",
                                },
                              }}
                            >
                              Eliminar
                            </Button>
                          </Stack>
                        </Stack>

                        {index !== memberMemberships.length - 1 && (
                          <Divider sx={{ mt: 1.2, borderColor: "rgba(148,163,184,0.14)" }} />
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.2, pt: 1 }}>
          <Button
            variant="outlined"
            onClick={closeMembershipModal}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}